import {Component, OnInit} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {first, map, switchMap} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {AssetInput, Asset } from "../../../mgmt-api-client/model";
import {AssetService} from "../../../mgmt-api-client";
import {AssetEditorDialog} from "../asset-editor-dialog/asset-editor-dialog.component";
import {ConfirmationDialogComponent, ConfirmDialogModel} from "../confirmation-dialog/confirmation-dialog.component";
import {NotificationService} from "../../services/notification.service";


@Component({
  selector: 'edc-demo-asset-viewer',
  templateUrl: './asset-viewer.component.html',
  styleUrls: ['./asset-viewer.component.scss']
})
export class AssetViewerComponent implements OnInit {

  filteredAssets$: Observable<Asset[]> = of([]);
  searchText = '';
  isTransferring = false;
  private fetch$ = new BehaviorSubject(null);

  constructor(private assetService: AssetService,
              private notificationService: NotificationService,
              private readonly dialog: MatDialog) {
}

  private showError(error: string, errorMessage: string) {
    this.notificationService.showError(errorMessage);
    console.error(error);
  }

  ngOnInit(): void {
    this.filteredAssets$ = this.fetch$
      .pipe(
        switchMap(() => {
          const assets$ = this.assetService.requestAssets();
          const normalizedSearch = this.searchText.trim().toLowerCase();

          return normalizedSearch
            ? assets$.pipe(map(assets => assets.filter(asset => {
              const name = (asset.properties.optionalValue<string>('edc', 'name') || '').toLowerCase();
              const edcId = (asset.properties.optionalValue<string>('edc', 'id') || '').toLowerCase();
              const assetId = (asset.id || '').toLowerCase();

              return name.includes(normalizedSearch) || edcId.includes(normalizedSearch) || assetId.includes(normalizedSearch);
            })))
            : assets$;
        }));
  }

  isBusy() {
    return this.isTransferring;
  }

  getDataAddress(asset: Asset): any | undefined {
    const rawAsset = asset as any;
    return rawAsset?.dataAddress;
  }

  getDataAddressField(dataAddress: any, fieldName: string): string | undefined {
    if (!dataAddress) {
      return undefined;
    }

    if (typeof dataAddress.optionalValue === 'function') {
      return dataAddress.optionalValue('edc', fieldName) ?? dataAddress[fieldName];
    }

    return dataAddress[fieldName];
  }

  getDataAddressType(dataAddress: any): string | undefined {
    return dataAddress?.['@type'] || this.getDataAddressField(dataAddress, 'type');
  }

  onSearch() {
    this.fetch$.next(null);
  }

  onDelete(asset: Asset) {
    const dialogData = ConfirmDialogModel.forDelete("asset", `"${asset.id}"`)
//    const ref = this.dialog.open(ConfirmationDialogComponent, {maxWidth: "20%", data: dialogData});
    const ref = this.dialog.open(ConfirmationDialogComponent, {disableClose: true, data: dialogData});

    ref.afterClosed().subscribe({
      next: res => {
        if (res) {
          this.assetService.removeAsset(asset.id).subscribe({
            next: () => this.fetch$.next(null),
            error: err => this.showError(err, "This asset cannot be deleted"),
            complete: () => this.notificationService.showInfo("Successfully deleted")
          });
        }
      }
    });
  }

  onCreate(event?: Event) {
    const triggerButton = event?.currentTarget as HTMLElement | null;
    triggerButton?.blur();

    const dialogRef = this.dialog.open(AssetEditorDialog, {disableClose: true, restoreFocus: false});
    dialogRef.afterClosed().pipe(first()).subscribe((result: { assetInput?: AssetInput }) => {
      triggerButton?.blur();
      const newAsset = result?.assetInput;
      if (newAsset) {
        this.assetService.createAsset(newAsset).subscribe({
          next: ()=> this.fetch$.next(null),
          error: err => this.showError(err, "This asset cannot be created"),
          complete: () => this.notificationService.showInfo("Successfully created"),
        })
      }
  })
}
}
